import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HeartIcon, ShareIcon, StarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { Dialog, Transition } from '@headlessui/react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import PageWrapper from '../components/layout/PageWrapper';

interface PropertyDetailsProps {}

const PropertyDetails: React.FC<PropertyDetailsProps> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkIn, setCheckIn] = useState<Date | null>(null);
  const [checkOut, setCheckOut] = useState<Date | null>(null);
  const [guests, setGuests] = useState(1);
  const [showGallery, setShowGallery] = useState(false);

  const property = {
    id: '1',
    title: 'Luxury Villa with Ocean View',
    location: 'Batroun, Lebanon',
    price: 250,
    rating: 4.9,
    reviews: 128,
    description: 'Experience luxury living in this stunning villa overlooking the Mediterranean Sea. Features include a private pool, garden, and direct beach access.',
    images: [
      `https://picsum.photos/800/600?random=1`,
      `https://picsum.photos/800/600?random=2`,
      `https://picsum.photos/800/600?random=3`,
      `https://picsum.photos/800/600?random=4`,
      `https://picsum.photos/800/600?random=5`,
    ],
    amenities: [
      'Pool',
      'Beach Access',
      'Wi-Fi',
      'Kitchen',
      'Air Conditioning',
      'Free Parking',
      'BBQ Grill',
      'Ocean View'
    ],
    host: {
      name: 'Sarah',
      image: 'https://picsum.photos/100/100?random=10',
      rating: 4.8,
      responseTime: '1 hour',
      superhost: true
    }
  };

  const handleBooking = () => {
    if (!checkIn || !checkOut) {
      alert('Please select check-in and check-out dates');
      return;
    }
    navigate('/booking/confirm', { 
      state: { 
        propertyId: id,
        checkIn,
        checkOut,
        guests,
        totalPrice: calculateTotalPrice()
      }
    });
  };

  const calculateTotalPrice = () => {
    if (!checkIn || !checkOut) return 0;
    const nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    return property.price * nights;
  };

  return (
    <PageWrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">{property.title}</h1>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <StarIcon className="h-5 w-5 text-yellow-400" />
                <span className="ml-1 font-medium">{property.rating}</span>
                <span className="mx-1">·</span>
                <span className="text-gray-500">{property.reviews} reviews</span>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-gray-500" />
                <span className="ml-1 text-gray-500">{property.location}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setIsFavorite(!isFavorite)}
                className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {isFavorite ? (
                  <HeartIconSolid className="h-5 w-5 text-red-500" />
                ) : (
                  <HeartIcon className="h-5 w-5" />
                )}
                <span>Save</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors">
                <ShareIcon className="h-5 w-5" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="grid grid-cols-4 gap-2 mb-8 rounded-xl overflow-hidden">
          <div className="col-span-2 row-span-2 relative">
            <img
              src={property.images[0]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
          </div>
          {property.images.slice(1, 5).map((image, index) => (
            <div key={index} className="relative">
              <img
                src={image}
                alt={`${property.title} - ${index + 1}`}
                className="w-full h-48 object-cover"
              />
            </div>
          ))}
          <button
            onClick={() => setShowGallery(true)}
            className="absolute bottom-4 right-4 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            Show all photos
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-3 gap-12">
          <div className="col-span-2">
            {/* Property Details */}
            <div className="border-b border-gray-200 pb-8 mb-8">
              <h2 className="text-2xl font-semibold mb-4">About this place</h2>
              <p className="text-gray-600 leading-relaxed">{property.description}</p>
            </div>

            {/* Amenities */}
            <div className="border-b border-gray-200 pb-8 mb-8">
              <h2 className="text-2xl font-semibold mb-4">What this place offers</h2>
              <div className="grid grid-cols-2 gap-4">
                {property.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-600">✓</span>
                    </div>
                    <span>{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Host Information */}
            <div>
              <h2 className="text-2xl font-semibold mb-4">Meet your host</h2>
              <div className="flex items-center gap-4">
                <img
                  src={property.host.image}
                  alt={property.host.name}
                  className="w-16 h-16 rounded-full"
                />
                <div>
                  <h3 className="font-medium">{property.host.name}</h3>
                  <p className="text-gray-500">Superhost · {property.host.rating} rating</p>
                  <p className="text-gray-500">Usually responds within {property.host.responseTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Panel */}
          <div className="relative">
            <div className="sticky top-24 bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <span className="text-2xl font-semibold">${property.price}</span>
                  <span className="text-gray-500"> / night</span>
                </div>
                <div className="flex items-center">
                  <StarIcon className="h-5 w-5 text-yellow-400" />
                  <span className="ml-1">{property.rating}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in</label>
                  <DatePicker
                    selected={checkIn}
                    onChange={(date) => setCheckIn(date)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
                    placeholderText="Select date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out</label>
                  <DatePicker
                    selected={checkOut}
                    onChange={(date) => setCheckOut(date)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
                    placeholderText="Select date"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Guests</label>
                <select
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <option key={num} value={num}>
                      {num} {num === 1 ? 'guest' : 'guests'}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={handleBooking}
                className="w-full bg-airbnb-red text-white py-3 rounded-lg font-medium hover:bg-red-600 transition-colors"
              >
                Book now
              </button>

              {checkIn && checkOut && (
                <div className="mt-4">
                  <div className="flex justify-between py-2">
                    <span>Total</span>
                    <span className="font-semibold">${calculateTotalPrice()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default PropertyDetails;